import { useEffect } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { Hero } from "@/components/wedding/Hero";
import { Venue } from "@/components/wedding/Venue";
import { Rsvp } from "@/components/wedding/Rsvp";
import { Footer } from "@/components/wedding/Footer";
import { MusicPlayer } from "@/components/wedding/MusicPlayer";
import { ScrollCue } from "@/components/wedding/ScrollCue";

function App() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Respect intentional anchor links (e.g. shared .../#venue)
    if (window.location.hash) return;

    let userInteracted = false;
    const markUser = () => {
      userInteracted = true;
    };
    const enforceTop = () => {
      if (userInteracted || window.__userScrolled) return;
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
      }
    };

    window.scrollTo(0, 0);
    window.addEventListener("load", enforceTop);
    // Mobile Chrome (typed-address navigation) can nudge scroll down after assets
    // settle — keep snapping back to top until the guest actually scrolls (max 8s).
    window.addEventListener("scroll", enforceTop, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("scroll", enforceTop);
      window.visualViewport.addEventListener("resize", enforceTop);
    }
    ["touchmove", "wheel", "keydown"].forEach((evt) =>
      window.addEventListener(evt, markUser, { passive: true })
    );
    // Typed-address navigation on mobile Chrome restores the old scroll offset
    // silently (no scroll event), so poll for the first 3s as a hard guarantee.
    const poll = setInterval(enforceTop, 100);
    const stopPoll = setTimeout(() => clearInterval(poll), 3000);
    const stop = setTimeout(() => {
      window.removeEventListener("scroll", enforceTop);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("scroll", enforceTop);
        window.visualViewport.removeEventListener("resize", enforceTop);
      }
    }, 8000);

    return () => {
      window.removeEventListener("load", enforceTop);
      window.removeEventListener("scroll", enforceTop);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("scroll", enforceTop);
        window.visualViewport.removeEventListener("resize", enforceTop);
      }
      ["touchmove", "wheel", "keydown"].forEach((evt) =>
        window.removeEventListener(evt, markUser)
      );
      clearTimeout(stop);
      clearInterval(poll);
      clearTimeout(stopPoll);
    };
  }, []);

  useEffect(() => {
    // Init Lenis one frame later so it captures scrollY = 0 as its starting state
    let lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    const initId = requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      lenis = new Lenis({ duration: 1.4, smoothWheel: true });
      window.__lenis = lenis;
      rafId = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(initId);
      cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
    };
  }, []);

  // Mobile Chrome (typed-address navigation) lays the page out from the top of the
  // SCREEN while only clientHeight is actually visible: innerHeight(852) > clientHeight(665).
  // In that state the browser bars overlay the page, so shift the content down by the
  // measured top-bar height. When the browser behaves (innerH == clientH) the offset is 0.
  useEffect(() => {
    const root = document.documentElement;
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)";
    document.body.appendChild(probe);

    const apply = () => {
      const gap = window.innerHeight - root.clientHeight;
      if (gap <= 8) {
        root.style.setProperty("--ui-top", "0px");
        root.style.setProperty("--ui-bottom", "0px");
        return;
      }
      const safeBottom = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
      // Chrome's bottom toolbar (+ home indicator when reported)
      const bottomBar = safeBottom > 0 ? safeBottom + 46 : gap * 0.43;
      root.style.setProperty("--ui-top", `${Math.max(0, Math.min(gap, gap - bottomBar))}px`);
      // fixed elements are laid out against the tall viewport in this state, so
      // bottom-anchored buttons must clear the toolbar themselves
      root.style.setProperty("--ui-bottom", `${Math.round(bottomBar)}px`);
    };

    apply();
    const poll = setInterval(apply, 200);
    const stopPoll = setTimeout(() => clearInterval(poll), 4000);
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      clearInterval(poll);
      clearTimeout(stopPoll);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      probe.remove();
      root.style.removeProperty("--ui-top");
      root.style.removeProperty("--ui-bottom");
    };
  }, []);

  return (
    <div
      className="App grain bg-[#FBF9F5] overflow-x-clip min-[1232px]:mx-[15vw]"
      dir="rtl"
      style={{ paddingTop: "var(--ui-top, 0px)", paddingBottom: "var(--ui-bottom, 0px)" }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-y-0 right-0 z-40 hidden min-[1232px]:block w-[15vw] bg-white border-l border-[#E6DEC8] shadow-[-20px_0_50px_-30px_rgba(26,22,18,0.15)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-y-0 left-0 z-40 hidden min-[1232px]:block w-[15vw] bg-white border-r border-[#E6DEC8] shadow-[20px_0_50px_-30px_rgba(26,22,18,0.15)]"
      />
      <Toaster position="top-center" richColors />
      <MusicPlayer />
      <ScrollCue />
      <Hero />
      <Venue />
      <Rsvp />
      <Footer />
    </div>
  );
}

export default App;
